import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";

const DEFAULTS = {
  brand_name: "Verifine Wines",
  tagline: "Curate the cellar. Verify the bottle.",
  portal_overline: "Private Reserve",
  portal_heading: "Enter your code",
  portal_subtitle: "Enter the code that came with your bottle to unlock its certificate, tasting notes and provenance.",
  portal_footer: "Verified • Authentic • Confidential",
  logo_url: "https://customer-assets.emergentagent.com/job_secure-access-167/artifacts/mmleicge_Logo%20com%20qualidade.png",
  public_url: "",
  accent_color: "#C5A059",
};

const SettingsContext = createContext({ settings: DEFAULTS, refresh: () => {}, loading: true });

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function resolveLogoUrl(url) {
  if (!url) return DEFAULTS.logo_url;
  if (url.startsWith("/api/")) return `${BACKEND_URL}${url}`;
  return url;
}

export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/settings");
      const merged = { ...DEFAULTS, ...data };
      merged.logo_url = resolveLogoUrl(merged.logo_url);
      setSettings(merged);
      if (typeof document !== "undefined" && merged.brand_name) {
        document.title = `${merged.brand_name} — ${merged.portal_overline}`;
      }
    } catch (e) {
      // Network or backend down — fall back to defaults; do not block the UI.
      if (typeof console !== "undefined") console.warn("[settings] Could not load remote settings, using defaults:", e?.message || e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const value = useMemo(() => ({ settings, refresh, loading }), [settings, refresh, loading]);

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  return useContext(SettingsContext);
}

export function publicCodeUrl(settings, code) {
  const origin = settings?.public_url || (typeof window !== "undefined" ? window.location.origin : "");
  return `${origin.replace(/\/$/, "")}/?code=${encodeURIComponent(code)}`;
}
